import { Button } from '@festina-life/ui/actions'
import {
  HeadlessUiCombobox,
  HeadlessUiComboboxInput,
  HeadlessUiComboboxOptions,
  HeadlessUiPopover,
  HeadlessUiPopoverButton,
  HeadlessUiPopoverPanel,
  isHotkey,
  RawTextInput,
} from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { paperStyles } from '@festina-life/ui/libs'
import { Icon } from '@festina-life/ui/structure'
import cx from 'classnames'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import { CategoryGroup } from './CategoryGroup'
import { CategoryOption } from './CategoryOption'
import { Option } from './Option'
import type {
  RankedItem,
  RenderedCategory,
  SearchableOption,
  SimpleSearchableItem,
} from './types'
import { useCommandPaletteLocalStorage } from './useCommandPaletteLocalStorage'
import { processCategoryGroup } from './utils'

type CategorisedItems = {
  items: RankedItem[] | undefined
  category: RenderedCategory
}[]

type Props = {
  search: string
  // We are already tied from the type to SearchableItemGroup, so having rank seems like a waste
  options: RankedItem[] // Would be nice to change/remove this.
  fallbackOptions: SearchableOption[]
  onChange: (nextSearchTerm: string) => void
  placeholder: string
  categories: {
    command: Record<string, RenderedCategory>
    navigation: Record<string, RenderedCategory>
    tenantNavigation: Record<string, RenderedCategory>
    recent: Record<string, RenderedCategory>
  }
}

export const CommandPalette = ({
  search,
  options,
  onChange,
  fallbackOptions,
  placeholder,
  categories,
}: Props) => {
  const translate = useTranslate()
  const navigate = useNavigate()
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const [isPerformingAction, setIsPerformingAction] = useState(false)

  const [favourites] = useCommandPaletteLocalStorage()

  const categorisedOptions = useMemo(() => {
    const processedOptions: {
      command: CategorisedItems
      navigation: CategorisedItems
      tenant: RankedItem[]
      recent: CategorisedItems
    } = {
      command: [],
      navigation: [],
      tenant: [],
      recent: [],
    }

    // favourites is added directly here, so that no one can accidentially add it directly on an action.
    const favouriteCategory = {
      favourites: {
        label: translate('enum.SearchCategories.favourites'),
        Icon: 'Star',
      },
    } as const

    processedOptions.command = processCategoryGroup({
      group: 'command',
      categories: { ...favouriteCategory, ...categories.command },
      options,
      shouldSortOnTitle: true,
      ...favourites.command,
    })

    processedOptions.navigation = processCategoryGroup({
      group: 'navigation',
      categories: {
        ...favouriteCategory,
        ...categories.navigation,
        ...categories.tenantNavigation,
      },
      options,
      shouldSortOnTitle: false,
      ...favourites.navigation,
    })

    const tenantOptions = options.filter((x) => x.group === 'tenant')
    processedOptions.tenant.push(...tenantOptions)

    processedOptions.recent = processCategoryGroup({
      group: 'recent',
      categories: categories.recent,
      options,
      shouldSortOnTitle: false,
      shouldHideFavourites: true,
      shouldHideWarnings: true,
      shouldHideUnauthorized: true,
      favouriteKeys: [], // It doesn't make sense to favourite a recent item, so we just don't add the category at all for this group.
    })

    return processedOptions
  }, [
    categories.command,
    categories.navigation,
    categories.tenantNavigation,
    favourites.command,
    favourites.navigation,
    options,
    translate,
    categories.recent,
  ])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isHotkey('mod+k', event)) {
        event.preventDefault() // extra safety preventing e.g. browser from picking it up
        event.stopPropagation()
        menuButtonRef.current?.click() // toggle the menu
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChange = async (
    option: SimpleSearchableItem | undefined,
    close: () => void,
  ) => {
    if (!option) {
      // The option can be undefined when the user types something and then deletes it again.
      return
    }

    setIsPerformingAction(true)
    try {
      if (option.action) {
        if (typeof option.action === 'function') {
          await option.action()
        } else {
          await navigate(option.action)
        }
      }
    } finally {
      setIsPerformingAction(false)
    }

    if (option.shouldPreventClose) {
      return
    }
    close()
  }

  const handleInputReset = () => onChange('')

  return (
    <HeadlessUiPopover className="relative" data-testid="commandPalette">
      {({ open, close }) => (
        <>
          <HeadlessUiPopoverButton
            as={Button}
            ref={menuButtonRef}
            data-testid="commandButton"
            onClick={handleInputReset}
            variant="discreet"
            className="flex gap-2"
          >
            {open ? (
              <Icon name="XMark" size="md" color="unset" />
            ) : (
              <Icon name="Bars3" size="md" color="unset" />
            )}
            <Icon name="MagnifyingGlass" size="md" color="unset" />
          </HeadlessUiPopoverButton>
          {open && (
            <HeadlessUiPopoverPanel static>
              <div // Backdrop
                className={cx(
                  'fixed inset-0 top-(--topBar-height) bg-gray-900 opacity-50',
                )}
                onClick={() => {
                  close(document.body)
                  handleInputReset()
                }}
              />
              <div
                className={cx(
                  'fixed bottom-0 left-0 top-(--topBar-height) grid w-full max-w-375 grid-cols-4 grid-rows-[auto_minmax(0px,1fr)]',
                  paperStyles({ borders: ['top'] }),
                )}
              >
                <HeadlessUiCombobox
                  onChange={(option) =>
                    handleChange(option as SimpleSearchableItem, () => {
                      close(document.body)
                      handleInputReset()
                    })
                  }
                  disabled={isPerformingAction}
                >
                  <div className="col-span-full grid grid-cols-subgrid py-2 px-1 gap-2">
                    <HeadlessUiComboboxInput
                      as={RawTextInput}
                      value={search}
                      placeholder={placeholder}
                      onChange={(event) => onChange(event.target.value ?? '')}
                      autoFocus // critical for UX
                      data-testid="commandPaletteInput"
                      isLoading={isPerformingAction}
                    />
                  </div>
                  <HeadlessUiComboboxOptions static className="contents">
                    {options.length === 0 && search !== '' ? (
                      <div>
                        {fallbackOptions.map((option) => (
                          <Option value={option} key={option.title} />
                        ))}
                      </div>
                    ) : (
                      <>
                        <CategoryGroup
                          group="command"
                          categoryAndItems={categorisedOptions.command}
                        />
                        <CategoryGroup
                          group="navigation"
                          categoryAndItems={categorisedOptions.navigation}
                        />
                        <CategoryGroup
                          group="recent"
                          categoryAndItems={categorisedOptions.recent}
                        />
                        <CategoryOption
                          group="tenant"
                          items={categorisedOptions.tenant}
                        />
                      </>
                    )}
                  </HeadlessUiComboboxOptions>
                </HeadlessUiCombobox>
              </div>
            </HeadlessUiPopoverPanel>
          )}
        </>
      )}
    </HeadlessUiPopover>
  )
}
